import React, { useContext, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import { useForm } from "react-hook-form";
import emailjs from "emailjs-com";
import { styled } from "@mui/material/styles";
import { ThemeContext } from "./../../../context/ThemeContext";

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#55d6aa",
  color: "#242424",
  fontWeight: "bold",
  "&:hover": {
    backgroundColor: "#48c099",
  },
}));

const StyledTextField = styled(TextField)({
  width: "100%",
  // Set the input field height
  "& .MuiOutlinedInput-root": {
    height: "5vh", // Set height to 5% of viewport height
    display: "flex",
    alignItems: "center", // Align text vertically
    "& fieldset": {
      border: "1px solid black",
    },
    "&.Mui-focused fieldset": {
      border: "2px solid #55d6aa", // Border color when focused
    },
  },

  // Style the input (text inside)
  "& .MuiOutlinedInput-input": {
    padding: "0 14px", // Adjust padding
    fontSize: "1rem", // Default text size
    "&::placeholder": {
      color: "gray", // Placeholder color
      fontSize: "0.9rem", // Placeholder font size
      opacity: 1, // Make sure placeholder is visible
    },
  },

  // Default Label Style
  "& .MuiInputLabel-root": {
    fontSize: "1rem", // Default label size
    top: "50%", // Align vertically
    transform: "translate(14px, -50%)", // Center label
    transition: "all 0.2s ease-in-out", // Smooth transition
  },

  // Focused Label Style
  "& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-shrink": {
    fontSize: "0.75rem", // Reduce size on focus
    transform: "translate(14px, -27px) scale(1)", // Move up
    fontWeight: "bold", // Make bold on focus
    color: "#242424", // Change color on focus
    transition: "all 0.2s ease-in-out", // Smooth animation
  },
});

const MultilinedTextField = styled(TextField)({
  width: "100%",
  // Set the input field height
  "& .MuiOutlinedInput-root": {
    display: "flex",
    alignItems: "center", // Align text vertically
    "& fieldset": {
      border: "1px solid black",
    },
    "&.Mui-focused fieldset": {
      border: "2px solid #55d6aa", // Border color when focused
    },
  },

  // Style the input (text inside)
  "& .MuiOutlinedInput-input": {
    // padding: "0 14px", // Adjust padding
    fontSize: "1rem", // Default text size
    "&::placeholder": {
      color: "gray", // Placeholder color
      fontSize: "0.9rem", // Placeholder font size
      opacity: 1, // Make sure placeholder is visible
    },
  },

  "& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-shrink": {
    fontSize: "0.75rem", // Reduce size on focus
    transform: "translate(14px, -8px) scale(1)", // Move up
    fontWeight: "bold", // Make bold on focus
    color: "#242424", // Change color on focus
    transition: "all 0.2s ease-in-out", // Smooth animation
  },
});

const Textfield = ({ label, register, error, helperText }) => {
  return (
    // <Textfield label={label} {...register} error={error} helperText={helperText} />

    <Box sx={{ height: "8vh" }}>
      <StyledTextField label={label} {...register} />
      {error && (
        <Typography sx={{ fontSize: "0.8rem", color: "red", ml: 1 }}>
          {error}
        </Typography>
      )}
      {helperText && (
        <Typography sx={{ fontSize: "0.8rem", color: "red", ml: 1 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

const ContactPopupForm = () => {
  const { openPopUp, setOpenPopUp } = useContext(ThemeContext);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true); // disable the button and prevent spamming

    const emailData = {
      user_name: `${data.firstName} ${data.lastName}`,
      user_email: data.email,
      user_company: data.company,
      user_message: data.description,
    };

    try {
      const response = await emailjs.send(
        "service_kukzfwd",
        "template_qe4hs2w",
        emailData,
        "oiScglFqfosxx4XAJ"
      );

      setSnackbar({
        open: true,
        message: "Enquiry sent successfully!",
        severity: "success",
      });

      reset();
    } catch (error) {
      console.error("EmailJS Error:", error);

      setSnackbar({
        open: true,
        message: "Failed to send enquiry. Try again later.",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false); // re-enable the button
    }
  };

  return (
    <>
      {/* Popup Dialog */}
      <Dialog
        open={openPopUp}
        onClose={() => {
          setOpenPopUp(false);
          reset();
        }}
        maxWidth="sm"
        fullWidth
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)', // Optional light background
          },
        }}
        PaperProps={{
          sx: {
            boxShadow: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#55d6aa",
            color: "#242424",
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          Submit an Enquiry
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ mt: 2 }}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Textfield
                  label="First Name*"
                  register={register("firstName", {
                    required: "First Name is required",
                  })}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Textfield
                  label="Last Name*"
                  register={register("lastName", {
                    required: "Last Name is required",
                  })}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Textfield
                  label="Email*"
                  register={register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Invalid email",
                    },
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Textfield
                  label="Company Name*"
                  register={register("company", {
                    required: "Company Name is required",
                  })}
                  error={!!errors.company}
                  helperText={errors.company?.message}
                />
              </Grid>

              {/* <Grid item xs={12} sm={6}> */}
              <MultilinedTextField
                fullWidth
                label="Description*"
                {...register("description", {
                  required: "Message is required",
                  minLength: {
                    value: 10,
                    message: "Message must be at least 10 characters",
                  },
                })}
                error={!!errors.description}
                helperText={errors.description?.message}
                multiline
                rows={4}
              />
              {/* </Grid> */}
            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setOpenPopUp(false)}
              sx={{
                color: "#242424",
                borderColor: "#242424",
                "&:hover": { backgroundColor: "#f0f0f0" },
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              sx={{
                backgroundColor: "#ffdd24",
                color: "#242424",
                fontWeight: "bold",
                "&:hover": { backgroundColor: "#e6c91f" },
              }}
              variant="contained"
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default ContactPopupForm;
